/**
 * The review results a person can record on a CSI-Tipalti Analysis row (AB#6872).
 * One source of truth for the filter, the bulk bar, the row review form and the chips.
 *
 * `value` is the string STORED on the record and matched EXACTLY (case-sensitive) by the
 * server's Review filter, so it must never change. `label` is display-only.
 *
 * PURE module: no React, MUI, axios or auth imports.
 */
import type {
  IconKey,
  StatusMeta,
  Tone,
} from "../../components/ui/statusLabels";

/** The seven stored review strings, exactly as the server stores and filters them. */
export const REVIEW_RESULT_VALUES = [
  "No Further Action",
  "Need Additional Information",
  "Waiting on Applicant",
  "Duplicate Payee Record",
  "Pending Review",
  "On Hold",
  "Mis-Matched Contact Info",
] as const;

export type ReviewResultValue = (typeof REVIEW_RESULT_VALUES)[number];

export interface ReviewResultOption {
  /** Stored value; '' means no review result (None). */
  value: ReviewResultValue | "";
  /** Sentence-case label shown on screen. */
  label: string;
  tone: Tone;
  icon: IconKey;
}

/** Shown for an empty review result: the row has not been reviewed. */
export const NOT_REVIEWED: ReviewResultOption = {
  value: "",
  label: "Not reviewed",
  tone: "neutral",
  icon: "schedule",
};

/** The seven review results in the order the screens have always listed them. */
export const REVIEW_RESULTS: ReviewResultOption[] = [
  {
    value: "No Further Action",
    label: "No further action",
    tone: "ok",
    icon: "check",
  },
  {
    value: "Need Additional Information",
    label: "Need additional information",
    tone: "warn",
    icon: "info",
  },
  {
    value: "Waiting on Applicant",
    label: "Waiting on applicant",
    tone: "warn",
    icon: "hourglass",
  },
  {
    value: "Duplicate Payee Record",
    label: "Duplicate payee record",
    tone: "neutral",
    icon: "link",
  },
  {
    value: "Pending Review",
    label: "Pending review",
    tone: "info",
    icon: "review",
  },
  { value: "On Hold", label: "On hold", tone: "warn", icon: "pause" },
  {
    value: "Mis-Matched Contact Info",
    label: "Mis-matched contact info",
    tone: "err",
    icon: "error",
  },
];

/** Review filter values understood by the server besides the seven results. */
export const REVIEW_FILTER_ALL = "all";
export const REVIEW_FILTER_NONE = "none";

/** Options for the Review filter: Any, Not reviewed, then the seven results. */
export const REVIEW_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: REVIEW_FILTER_ALL, label: "Any" },
  { value: REVIEW_FILTER_NONE, label: NOT_REVIEWED.label },
  ...REVIEW_RESULTS.map(({ value, label }) => ({
    value: value as string,
    label,
  })),
];

/** Options for the row review form: None ('') then the seven results. */
export const REVIEW_FORM_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "None" },
  ...REVIEW_RESULTS.map(({ value, label }) => ({
    value: value as string,
    label,
  })),
];

/** True when `value` is exactly one of the seven stored strings. */
export function isReviewResultValue(
  value: string | null | undefined
): value is ReviewResultValue {
  return (REVIEW_RESULT_VALUES as readonly string[]).includes(value ?? "");
}

/** The option for a stored value (exact match first, then case-insensitive), if any. */
export function findReviewResult(
  value: string | null | undefined
): ReviewResultOption | undefined {
  const raw = (value ?? "").trim();
  if (!raw) return NOT_REVIEWED;
  const exact = REVIEW_RESULTS.find((option) => option.value === raw);
  if (exact) return exact;
  const lower = raw.toLowerCase();
  return REVIEW_RESULTS.find((option) => option.value.toLowerCase() === lower);
}

/**
 * Chip label, tone and icon for a stored review result. Empty -> "Not reviewed"
 * (neutral); an unrecognised value shows its own text in a neutral chip.
 */
export function reviewResultMeta(value: string | null | undefined): StatusMeta {
  const option = findReviewResult(value);
  if (option) {
    return { label: option.label, tone: option.tone, icon: option.icon };
  }
  return { label: (value ?? "").trim(), tone: "neutral", icon: "help" };
}
